import './styles/base.css'
import './styles/screens.css'

import { unlock } from './core/audio'
import { clear } from './core/dom'
import { scheduleNextReminder } from './core/install'
import { leaveSignal, startRouter, type Route } from './core/router'
import { getSettings, updateSettings } from './core/store'
import { startWarmShiftClock } from './core/theme'
import { detectLang, setLang } from './i18n'

import { renderBreath } from './screens/breath'
import { renderCheckin } from './screens/checkin'
import { renderCompanion } from './screens/companion'
import { renderDrift } from './screens/drift'
import { renderHome } from './screens/home'
import { renderLedger } from './screens/ledger'
import { renderMantra } from './screens/mantra'
import { renderNight3am } from './screens/night3am'
import { renderOnboarding } from './screens/onboarding'
import { renderSafety } from './screens/safety'
import { renderSettings } from './screens/settings'
import { renderSound } from './screens/sound'

const app = document.getElementById('app')!

// Language comes from storage once onboarding has run, and from the browser
// before that. The detected value is written back to settings immediately —
// otherwise the language toggle would show 'English' selected on a screen that
// is already rendering in Persian.
const saved = getSettings()
const lang = saved.onboarded ? saved.lang : detectLang()
setLang(lang)
if (lang !== saved.lang) updateSettings({ lang })

startWarmShiftClock()
scheduleNextReminder()

// Audio contexts start suspended until a gesture. Unlocking on the first touch
// means the breath cue plays on the first round rather than the second.
const unlockOnce = (): void => {
  void unlock()
  window.removeEventListener('pointerdown', unlockOnce)
}
window.addEventListener('pointerdown', unlockOnce, { once: true })

function render(route: Route): void {
  clear(app)
  app.scrollTop = 0
  window.scrollTo(0, 0)

  // 3AM mode paints its own colour scheme; every other route clears it, so
  // navigating away from the rescue flow always restores the normal palette.
  if (route !== 'night3am' && route !== 'safety') {
    delete document.documentElement.dataset.mode
  }

  const signal = leaveSignal()

  switch (route) {
    case 'onboarding':
      return renderOnboarding(app)
    case 'breath':
      return renderBreath(app, signal)
    case 'mantra':
      return renderMantra(app, signal)
    case 'drift':
      return renderDrift(app, signal)
    case 'sound':
      return renderSound(app)
    case 'night3am':
      return renderNight3am(app, signal)
    case 'ledger':
      return renderLedger(app)
    case 'checkin':
      return renderCheckin(app)
    case 'companion':
      return renderCompanion(app, signal)
    case 'settings':
      return renderSettings(app)
    case 'safety':
      return renderSafety(app)
    default:
      return renderHome(app, signal)
  }
}

// Deliberately no global store subscription. Screens that need to react to
// their own writes repaint themselves; a blanket repaint-on-change would tear
// down the breathing loop or the drift script mid-session, because those own
// long-running async work tied to the render that started them.
startRouter(render)
